import { useState } from "react";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Flag } from "lucide-react";
import { toast } from "sonner";

const REASONS = ["Fake profile", "Inappropriate photos", "Harassment", "Spam", "Other"];

export default function ReportDialog({ targetUserId }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState(REASONS[0]);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/reports", { target_user_id: targetUserId, reason, note });
      toast.success("Report submitted. Our team will review.");
      setOpen(false); setNote("");
    } catch (e) { toast.error(formatError(e)); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="rounded-full text-muted-foreground hover:text-destructive" data-testid="report-btn">
          <Flag className="w-4 h-4 mr-1"/>Report
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Report this profile</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Reason</Label>
            <RadioGroup value={reason} onValueChange={setReason} className="mt-2 space-y-1">
              {REASONS.map((r) => (
                <label key={r} htmlFor={`r-${r}`} className="flex items-center gap-3 cursor-pointer rounded-lg p-2 hover:bg-muted">
                  <RadioGroupItem value={r} id={`r-${r}`} data-testid={`reason-${r.replace(/\s+/g,'-').toLowerCase()}`} />
                  <span className="text-sm">{r}</span>
                </label>
              ))}
            </RadioGroup>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Details (optional)</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Anything our team should know..." rows={3} className="mt-1.5" data-testid="report-note" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={submitting} className="rounded-full bg-destructive hover:bg-destructive/90" data-testid="report-submit">{submitting ? "Submitting..." : "Submit report"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
